import type { TimeGranularity } from '../types/api';

export interface TokenBucketTotals {
  inputTokens: number;
  outputTokens: number;
  cacheCreationTokens: number;
  cacheReadTokens: number;
  totalTokens: number;
  contentTokens: number;
  costTokens: number;
}

export const emptyTokenTotals: TokenBucketTotals = {
  inputTokens: 0,
  outputTokens: 0,
  cacheCreationTokens: 0,
  cacheReadTokens: 0,
  totalTokens: 0,
  contentTokens: 0,
  costTokens: 0,
};

// tokenBucketKey maps a daily (YYYY-MM-DD, UTC) bucket string onto the bucket
// key for the requested chart granularity, mirroring the backend's bucketing
// rules (GetTimeSeriesWeekly/Monthly in db/queries.sql) so token-series rows
// merge cleanly with the rest of the chart data:
//  - daily: unchanged
//  - weekly: the Sunday on/before the date (DATE_TRUNC('week', d+1day)-1day)
//  - monthly: the 1st of that month
export function tokenBucketKey(dailyBucket: string, granularity: TimeGranularity): string {
  if (granularity === 'daily') return dailyBucket;

  const [y, m, d] = dailyBucket.split('-').map((n) => parseInt(n, 10));
  const date = new Date(Date.UTC(y, m - 1, d));

  if (granularity === 'monthly') {
    date.setUTCDate(1);
  } else {
    // weekly: roll back to the most recent Sunday.
    date.setUTCDate(date.getUTCDate() - date.getUTCDay());
  }

  return date.toISOString().slice(0, 10);
}

// aggregateTokenSeries buckets per-day token rows (from GetTokenSeries) into
// the requested granularity, summing days that fall in the same bucket. This
// keeps the token charts time-correct (sourced from the per-day rollup) at
// any granularity, not just daily.
export function aggregateTokenSeries(
  dataPoints: Array<{
    bucket: string;
    inputTokens?: string;
    outputTokens?: string;
    cacheCreationTokens?: string;
    cacheReadTokens?: string;
    totalTokens?: string;
    contentTokens?: string;
    costTokens?: string;
  }>,
  granularity: TimeGranularity
): Map<string, TokenBucketTotals> {
  const result = new Map<string, TokenBucketTotals>();
  for (const dp of dataPoints) {
    const key = tokenBucketKey(dp.bucket, granularity);
    const existing = result.get(key) ?? { ...emptyTokenTotals };
    existing.inputTokens += parseInt(dp.inputTokens || '0', 10);
    existing.outputTokens += parseInt(dp.outputTokens || '0', 10);
    existing.cacheCreationTokens += parseInt(dp.cacheCreationTokens || '0', 10);
    existing.cacheReadTokens += parseInt(dp.cacheReadTokens || '0', 10);
    existing.totalTokens += parseInt(dp.totalTokens || '0', 10);
    existing.contentTokens += parseInt(dp.contentTokens || '0', 10);
    existing.costTokens += parseInt(dp.costTokens || '0', 10);
    result.set(key, existing);
  }
  return result;
}

// aggregateTokenByModelSeries pivots the long-format per-day/per-model rows
// (from GetTokenSeriesByModel) into per-bucket, per-model total-token sums at
// the requested granularity. Mirrors aggregateTokenSeries, but keyed on
// (bucket, model): days that fall in the same bucket are summed per model, so
// the "Total Tokens by Model" stacked bar stays time-correct at any
// granularity. total_tokens already includes cache tokens, matching the
// "Total Tokens" card.
export function aggregateTokenByModelSeries(
  dataPoints: Array<{
    bucket: string;
    model: string;
    totalTokens?: string;
  }>,
  granularity: TimeGranularity
): Map<string, Map<string, number>> {
  const result = new Map<string, Map<string, number>>();
  for (const dp of dataPoints) {
    const key = tokenBucketKey(dp.bucket, granularity);
    const byModel = result.get(key) ?? new Map<string, number>();
    const total = parseInt(dp.totalTokens || '0', 10);
    byModel.set(dp.model, (byModel.get(dp.model) ?? 0) + total);
    result.set(key, byModel);
  }
  return result;
}
